import React, { useEffect, useMemo, useState } from "react";
import { Plus, Search } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../supabaseClient";
import AdminTopBar from "./AdminTopBar";
import SuccessToast from "../components/SuccessToast";
import { adminSearchInput, adminSearchWrap } from "./adminUi";
import { isMissingVenueTable } from "../lib/venuesApi";

const Venues = () => {
  const navigate = useNavigate();
  const [venues, setVenues] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showArchived, setShowArchived] = useState(false);
  const [search, setSearch] = useState("");
  const [toastMessage, setToastMessage] = useState("");
  const [errorToastMessage, setErrorToastMessage] = useState("");
  const [missingTable, setMissingTable] = useState(false);

  const loadVenues = async () => {
    setLoading(true);
    const { data, error } = await supabase
      .from("venues")
      .select(`
        id,
        name,
        address,
        is_archived,
        venue_sections (
          id,
          parent_section_id,
          venue_seats ( id )
        )
      `)
      .order("name", { ascending: true });

    if (error) {
      console.error("Failed to load venues:", error);
      if (isMissingVenueTable(error)) {
        setMissingTable(true);
        setVenues([]);
      } else {
        setErrorToastMessage("Failed to load venues.");
      }
      setLoading(false);
      return;
    }

    setMissingTable(false);
    setVenues(data || []);
    setLoading(false);
  };

  useEffect(() => {
    loadVenues();
  }, []);

  const tabVenues = useMemo(
    () => (venues || []).filter((venue) => (showArchived ? venue.is_archived : !venue.is_archived)),
    [venues, showArchived]
  );

  const visibleVenues = useMemo(() => {
    const needle = search.trim().toLowerCase();
    if (!needle) return tabVenues;
    return tabVenues.filter((venue) =>
      [venue.name, venue.address].some((value) => String(value || "").toLowerCase().includes(needle))
    );
  }, [tabVenues, search]);

  const summarizeVenue = (venue) => {
    const sections = venue.venue_sections || [];
    const topLevelCount = sections.filter((section) => !section.parent_section_id).length;
    const seatCount = sections.reduce(
      (total, section) => total + (section.venue_seats?.length || 0),
      0
    );
    return { topLevelCount, seatCount };
  };

  const archiveVenue = async (venue) => {
    const { error } = await supabase
      .from("venues")
      .update({ is_archived: !venue.is_archived })
      .eq("id", venue.id);

    if (error) {
      console.error("Failed to archive venue:", error);
      setErrorToastMessage(error.message || "Unable to update venue.");
      return;
    }

    setToastMessage(venue.is_archived ? "Venue restored." : "Venue archived.");
    loadVenues();
  };

  return (
    <div className="bg-[#f9f9f9] min-h-screen">
      <AdminTopBar />
      <div className="p-4 sm:p-6 md:p-10">
        <div className="max-w-7xl mx-auto min-w-0">
          <SuccessToast message={toastMessage} />
          <SuccessToast message={errorToastMessage} variant="error" />

          <div className="flex flex-col gap-3 sm:flex-row sm:justify-between sm:items-center mb-8">
            <h1 className="text-2xl md:text-[26px] font-black text-gray-900 tracking-tight uppercase italic min-w-0">
              Venues
            </h1>
            <div className="flex flex-wrap items-center gap-3 w-full sm:w-auto">
              <div className={adminSearchWrap}>
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  value={search}
                  onChange={(event) => setSearch(event.target.value)}
                  placeholder="Search venues..."
                  className={adminSearchInput}
                  aria-label="Search venues"
                />
              </div>
              <label className="flex items-center gap-2 text-xs font-bold uppercase text-gray-500">
                <input
                  type="checkbox"
                  checked={showArchived}
                  onChange={(event) => setShowArchived(event.target.checked)}
                />
                Show archived
              </label>
              <button
                type="button"
                onClick={() => navigate("/admin/venues/new")}
                className="bg-brand-maroon text-white px-4 py-2 rounded-lg text-xs font-bold uppercase flex items-center justify-center gap-2 w-full sm:w-auto"
              >
                <Plus size={14} /> Create Venue
              </button>
            </div>
          </div>

          {missingTable ? (
            <div className="rounded-2xl border border-amber-200 bg-amber-50 p-6 text-sm text-amber-900">
              Venue tables are not installed yet. Run
              {" "}
              <code className="font-mono">echo_jham_web/supabase/create_venue_management.sql</code>
              {" "}
              in the Supabase SQL editor, then refresh this page.
            </div>
          ) : loading ? (
            <p className="text-sm font-bold uppercase tracking-wide text-gray-500">Loading venues...</p>
          ) : visibleVenues.length === 0 ? (
            <div className="rounded-2xl border border-dashed border-gray-300 bg-white p-10 text-center text-sm text-gray-500">
              {search.trim() && tabVenues.length > 0
                ? "No venues match your search."
                : showArchived
                  ? "No archived venues."
                  : "No venues yet. Create a reusable venue to use in Event Creation."}
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
              {visibleVenues.map((venue) => {
                const { topLevelCount, seatCount } = summarizeVenue(venue);
                return (
                  <div key={venue.id} className="rounded-2xl border border-gray-200 bg-white p-5 shadow-sm">
                    <h2 className="text-lg font-black text-gray-900">{venue.name}</h2>
                    <p className="mt-1 text-sm text-gray-500">{venue.address || "No address"}</p>
                    <p className="mt-4 text-xs font-bold uppercase tracking-widest text-gray-400">
                      {topLevelCount} section{topLevelCount === 1 ? "" : "s"}
                    </p>
                    <p className="text-xs font-bold uppercase tracking-widest text-gray-400">
                      {seatCount} seat{seatCount === 1 ? "" : "s"}
                    </p>
                    {venue.is_archived ? (
                      <p className="mt-2 text-[11px] font-bold uppercase text-amber-700">Archived</p>
                    ) : null}
                    <div className="mt-5 flex flex-wrap items-center gap-2">
                      <button
                        type="button"
                        onClick={() => navigate(`/admin/venues/${venue.id}`)}
                        className="rounded-lg bg-black px-3 py-2 text-xs font-bold uppercase text-white"
                      >
                        View / Edit
                      </button>
                      <button
                        type="button"
                        onClick={() => archiveVenue(venue)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-xs font-bold uppercase text-gray-700"
                      >
                        {venue.is_archived ? "Restore" : "Archive"}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default Venues;
